import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTRPC } from "../../lib/api";
import { Button, ErrorText, TextField } from "../../ui";
import { useToast } from "../../ui/toast";
import { nextSlot, parseAttendees, scheduleWindow } from "./model";

const DURATIONS = [15, 30, 45, 60, 90];

/**
 * Schedule a meeting: local date, time and duration, attendees from the workspace's members plus
 * free-text addresses. Google sends the invitations; the Meet link is posted to the channel.
 */
export function ScheduleForm({
  workspaceId,
  channelId,
  accountId,
  title,
  memberEmails,
  onError,
  onBack,
  onDone,
  now = new Date(),
}: {
  workspaceId: string;
  channelId: string;
  accountId: string;
  title: string;
  memberEmails: readonly string[];
  onError: (e: unknown) => void;
  onBack: () => void;
  onDone: () => void;
  now?: Date;
}) {
  const trpc = useTRPC();
  const toast = useToast();
  const slot = nextSlot(now);
  const [date, setDate] = useState(slot.date);
  const [time, setTime] = useState(slot.time);
  const [minutes, setMinutes] = useState(30);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [extra, setExtra] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const schedule = useMutation(
    trpc.meet.schedule.mutationOptions({ onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.meet.list.pathKey() }) }),
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const span = scheduleWindow(date, time, minutes);
    const typed = parseAttendees(extra);
    if (span === null) return setProblem("Pick a date and a time.");
    if (typed.invalid.length > 0) return setProblem(`Not an email address: ${typed.invalid.join(", ")}`);
    if (title.length === 0) return setProblem("Give the meeting a title.");
    setProblem(null);
    onError(null);
    const attendees = [...new Set([...picked, ...typed.valid])];
    schedule.mutate(
      { workspaceId, accountId, title, start: span.start, end: span.end, attendees, postTo: { channelId } },
      {
        onSuccess: () => {
          toast({ message: attendees.length > 0 ? "Meeting scheduled; invitations sent and the link posted." : "Meeting scheduled; the link is posted." });
          onDone();
        },
        onError,
      },
    );
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Schedule a meeting">
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Date" type="date" value={date} required onChange={(e) => setDate(e.target.value)} />
        <TextField label="Time" type="time" value={time} required onChange={(e) => setTime(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="meet-duration" className="text-sm font-medium text-text">
          Duration
        </label>
        <select
          id="meet-duration"
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
          className="min-h-9 rounded-md border border-border bg-surface px-3 text-sm text-text"
        >
          {DURATIONS.map((m) => (
            <option key={m} value={m}>
              {m} minutes
            </option>
          ))}
        </select>
      </div>
      {memberEmails.length > 0 ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm font-medium text-text">Invite members</legend>
          {memberEmails.map((email) => (
            <label key={email} className="flex items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={picked.has(email)}
                onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(email);
                  else next.delete(email);
                  setPicked(next);
                }}
                className="size-4 accent-[var(--accent)]"
              />
              {email}
            </label>
          ))}
        </fieldset>
      ) : null}
      <TextField
        label="Other guests"
        hint="Email addresses, separated by commas or spaces."
        value={extra}
        onChange={(e) => setExtra(e.target.value)}
        placeholder="guest@example.com"
      />
      {problem === null ? null : <ErrorText>{problem}</ErrorText>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={schedule.isPending}>
          {schedule.isPending ? "Scheduling…" : "Schedule"}
        </Button>
        <Button variant="ghost" onClick={onBack} disabled={schedule.isPending}>
          Back
        </Button>
      </div>
    </form>
  );
}
